import { styled } from '@mui/material/styles';
import { alpha, Button, Card, CardProps, darken, IconButton, Paper, ToggleButton } from '@mui/material';

export const NodeRowCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'progress',
})<CardProps & { size?: number }>(({ theme, color, size = 5 }) => ({
  width: theme.spacing(size),
  height: theme.spacing(size),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: theme.spacing(size * 0.7),
  borderRadius: '10px',
  background: color,
  color: color ? theme.palette.getContrastText(color) : theme.palette.common.white,
}));

type DraggableListItemProps = {
  isDragging: boolean;
  color: string;
};

export const DraggableListItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'color',
})<DraggableListItemProps>(({ theme, isDragging, color }) => {
  const base = color || theme.palette.primary.main;
  return {
    background: alpha(base, isDragging ? 0.28 : 0.12),
    border: `1px solid ${alpha(base, isDragging ? 0.6 : 0.3)}`,
    boxShadow: isDragging ? `0 8px 24px ${alpha(base, 0.4)}` : 'none',
    padding: theme.spacing(1.25),
    borderRadius: '10px',
    cursor: 'grab',
    transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow']),
    '&:hover': {
      background: alpha(base, 0.2),
      borderColor: alpha(base, 0.5),
    },
  };
});

export const SchemaPropertyCard = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
}));

export const RemoveFieldButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.error.main, 0.08),
  transition: theme.transitions.create(['background-color', 'border-color', 'color', 'box-shadow']),
  '&:hover': {
    borderColor: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.16),
    boxShadow: `0 0 12px ${alpha(theme.palette.error.main, 0.3)}`,
  },
}));

export const AddFieldButton = styled(Button)(({ theme }) => ({
  alignSelf: 'flex-start',
  marginTop: theme.spacing(1),
  textTransform: 'none',
  borderRadius: '10px',
  borderStyle: 'dashed',
  borderColor: theme.palette.divider,
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(['background-color', 'border-color', 'color']),
  '&:hover': {
    borderStyle: 'dashed',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
}));

export const CodePreviewPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  backgroundImage: 'none',
}));

export const CanvasPreviewContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  height: 400,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
}));

export const PreviewToggle = styled(ToggleButton)(({ theme }) => ({
  padding: theme.spacing(0.75),
  borderRadius: '10px',
  lineHeight: 1,
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.85),
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
  transition: theme.transitions.create(['background-color', 'border-color', 'color', 'box-shadow']),
  '& svg': { fontSize: 18 },
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.5),
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    color: theme.palette.text.primary,
  },
  '&.Mui-selected': {
    color: theme.palette.accent.contrastText,
    backgroundColor: theme.palette.accent.main,
    borderColor: theme.palette.accent.main,
    boxShadow: `0 2px 12px ${alpha(theme.palette.accent.main, 0.45)}`,
    '&:hover': {
      color: theme.palette.accent.contrastText,
      backgroundColor: darken(theme.palette.accent.main, 0.12),
      borderColor: theme.palette.accent.main,
    },
  },
}));
